import React, { createContext, type ReactNode, useState } from 'react';
import { iconComponents } from '../components/Core/IconImports';
import { Form, Input, Modal } from 'antd';
import { Trans, useTranslation } from 'react-i18next';

interface ModalOptions {
  title: string;
  message?: string;
  icon?: keyof typeof iconComponents;
  content?: ReactNode;
  confirmButtonClassName?: string;
  iconClassName?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmKeyword?: string;
  destroyOnHidden?: boolean;
  className?: string;
  width?: number;
}

interface ModalContextProps {
  open: (options: ModalOptions) => Promise<void>;
  close: (confirmed: boolean, data?: any) => void;
}

export const ModalContext = createContext<ModalContextProps | undefined>(undefined);

export function ModalProvider({ children }: { children: ReactNode }) {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [modalState, setModalState] = useState<{
    visible: boolean;
    title?: string;
    message?: string;
    icon?: React.ReactNode;
    confirmLabel?: string;
    cancelLabel?: string;
    confirmKeyword?: string;
    content?: ReactNode;
    destroyOnHidden?: boolean;
    className?: string;
    width?: number;
    resolve?: (value: any) => void;
    reject?: (reason?: unknown) => void;
  }>({
    visible: false,
  });

  /**
   * Apre il Modal restituendo una Promise<void>.
   * Se l’utente conferma, la promise viene risolta.
   * Se annulla o chiude, viene rifiutata.
   */
  const open = (options: ModalOptions): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      setModalState({
        visible: true,
        title: options.title,
        message: options.message,
        icon: options.icon ? iconComponents[options.icon] : undefined,
        confirmLabel: options.confirmLabel,
        cancelLabel: options.cancelLabel,
        confirmKeyword: options.confirmKeyword,
        content: options.content,
        destroyOnHidden: options.destroyOnHidden || false,
        className: options.className,
        width: options.width,
        resolve,
        reject,
      });
      // If the keyword is required, reset the form
      if (options.confirmKeyword) {
        form.resetFields();
      }
    });
  };

  const handleOk = () => {
    if (modalState.confirmKeyword) {
      // Se è richiesta la keyword, validiamo il form
      form
        .validateFields()
        .then(() => {
          close(true);
        })
        .catch(() => {
          // La validazione mostra già gli errori, quindi non facciamo altro
        });
    } else {
      close(true);
    }
  };

  /**
   * Chiude il Modal: se `confirmed = true`, chiama resolve;
   * altrimenti chiama reject.
   */
  const close = (confirmed: boolean, data?: any) => {
    setModalState((prev) => {
      if (confirmed && prev.resolve) {
        prev.resolve(data);
      } else if (!confirmed && prev.reject) {
        prev.reject();
      }
      return { ...prev, visible: false, content: null };
    });
  };

  const hasCustomContent = !!modalState.content;

  return (
    <ModalContext.Provider value={{ open, close }}>
      {children}
      <Modal
        transitionName="ant-modal-slide-up"
        centered
        open={modalState.visible}
        title={
          <div className="pr-4">
            <Trans>{modalState.title}</Trans>
          </div>
        }
        width={modalState.width}
        onOk={handleOk}
        onCancel={() => close(false)}
        footer={hasCustomContent ? null : undefined}
        okText={modalState.confirmLabel || t('common.confirm')}
        cancelText={modalState.cancelLabel || t('common.cancel')}
        destroyOnHidden
        className={modalState.className}
      >
        <>
          {modalState.icon && <div style={{ marginBottom: '1rem' }}>{modalState.icon}</div>}
          {modalState.message && (
            <p>
              <Trans>{modalState.message}</Trans>
            </p>
          )}
          {modalState.content && modalState.content}
          {!modalState.content && modalState.confirmKeyword && (
            <Form
              form={form}
              size="large"
              layout="vertical"
              className="pt-6"
              onSubmitCapture={handleOk}
            >
              <Form.Item
                name="confirmation"
                rules={[
                  {
                    validator: (_, value) => {
                      if (value === modalState.confirmKeyword) {
                        return Promise.resolve();
                      }
                      return Promise.reject(t('errors.confirmationMessageMatch'));
                    },
                  },
                ]}
              >
                <Input autoFocus placeholder={t('common.confirmationMessage')} />
              </Form.Item>
            </Form>
          )}
        </>
      </Modal>
    </ModalContext.Provider>
  );
}
